import type { SubRenderState, SubRowVariant } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { blockedPresentation } from '../Blocking/blockedPresentation'
import { BlockedPreview } from '../Blocking/BlockedPreview'
import type { SubTemplateControls, SubTemplateDisplay, SubTemplateSlots } from './RowTemplateTypes'
import {
  SubChildren,
  SubCountLink,
  SubEmailFrom,
  SubFeed,
  SubHeader,
  SubIcon,
  SubIconStrip,
  SubMeta,
  SubText,
  SubTime,
  SubTitleLink,
  SubSelectionTitle,
  subDirectHref,
} from './RowTemplateParts'

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function scalarText(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true
    if (['0', 'false', 'no', 'off', ''].includes(normalized)) return false
  }
  return fallback
}

// Sender-address fallback for rows whose payload address is empty: a title
// that already looks like an address passes through; anything else becomes
// title@msgdesk.com. (Was duplicated in the Comments variants; the shared
// emailFrom control owns it now.)
function addressFromTitle(title: string): string {
  const value = String(title || '').replace(/^\s+|\s+$/g, '')
  if (!value) return ''
  return value.includes('@') ? value : `${value}@msgdesk.com`
}

export function useSubTemplateBridge(sub: SubRenderState, childVariant: SubRowVariant, sourceMode?: string): {
  display: SubTemplateDisplay
  controls: SubTemplateControls
  slots: SubTemplateSlots
} {
  const { state } = useSubsStore()
  // Folder pages (favs/drafts/trash) never open or load nested subs — child
  // counts render as plain numbers there (trash always behaved this way).
  const projectionMode = sourceMode || state.renderProjection.mode
  const isInteractive = projectionMode !== 'trash' && projectionMode !== 'favs' && projectionMode !== 'drafts'
  const identity = record(sub.legacy.data?.facade?.identity)
  const access = record(sub.access)
  const permissions = record(sub.permissions)
  const grant = sub.accessGrant
  const email = record(sub.email)
  const semanticDisplay = record(sub.display)
  const attachments = sub.attachments || []
  const isLocked = bool(access.is_locked)
  const isEditLocked = bool(access.is_edit_locked)
  const isTimeLocked = bool(access.is_time_locked)
  const isContentLocked = bool(access.is_content_locked, isTimeLocked || isLocked)
  const hasEditPin = bool(access.has_edit_pin)
  // BLOCKED-PRESENTATION: the shared helper is the only state decider. The
  // ordinary registered variant still renders; only its shared text leaf is
  // replaced with BlockedPreview.
  const blockedInfo = blockedPresentation({
    contentLocked: isContentLocked,
    timeLocked: isTimeLocked,
    timeSide: grant?.time_side,
  })
  const isBlocked = blockedInfo.blocked !== 'none'
  const rawIsEmpty = Array.isArray(semanticDisplay.is_empty) ? semanticDisplay.is_empty : []
  const isSubmsges = sub.legacy.data?.is_submsges_section === true
  // FOREIGN-FROM: the true creator of a foreign sub (serializer emits it
  // grants-gated on foreign rows only; see RowTemplateTypes).
  const fromRec = record(identity.from)
  const fromName = scalarText(fromRec.name)
  // ONE identity rule for author/address — the display contract and the
  // shared emailFrom control read the same values.
  const identityAuthor = sub.author || scalarText(identity.display_name) || scalarText(identity.name_key) || sub.codeKey || ''
  const identityAddress = scalarText(identity.address) || scalarText(email.from_display) || (sub.codeKey ? `${sub.codeKey}@msgdesk.com` : '')
  // Shared sender email line (skeleton-owned ELEMENT; variants opt in): the
  // canonical rule is the former Comments-variant superset — author enriched
  // by the email display sender, address deduped case-insensitively against
  // the author so the line never repeats itself.
  const emailAuthor = identityAuthor || scalarText(email.from_display) || identityAddress || sub.title || ''
  const emailAddress = identityAddress || addressFromTitle(sub.title)
  const emailAddressShown = emailAddress && emailAddress.toLowerCase() !== emailAuthor.toLowerCase() ? emailAddress : ''
  const emailFrom = emailAuthor || emailAddressShown
    ? <SubEmailFrom author={emailAuthor} address={emailAddressShown} />
    : null
  const check = <SubIcon sub={sub} kind="check" sourceMode={sourceMode} />
  const favorite = <SubIcon sub={sub} kind="fav" sourceMode={sourceMode} />
  const quickReply = isBlocked ? null : <SubIcon sub={sub} kind="qr" sourceMode={sourceMode} />
  const iconStrip = isBlocked ? <>{check}{favorite}</> : <SubIconStrip sub={sub} sourceMode={sourceMode} />
  const title = projectionMode === 'trash' ? <SubSelectionTitle sub={sub} /> : <SubTitleLink sub={sub} />
  const time = <SubTime sub={sub} />
  const header = <SubHeader sub={sub} />
  const text = isBlocked
    ? <BlockedPreview sub={sub} presentation={blockedInfo} hasEditPin={hasEditPin} />
    : <SubText sub={sub} />
  const answers = !isBlocked && sub.counts.answers > 0 ? (
    <SubCountLink
      sub={sub}
      typeId={0}
      count={sub.counts.answers}
      label="answers"
      isInteractive={isInteractive}
    />
  ) : null
  // §0.16 TWO-isMsg-SCOPES: submessages exist only under a message row. The
  // count is not proof enough — the row must positively be a message
  // (fail-closed; sub.isMsg is the normalized boolean from the row payload).
  const rowCanHaveSubmessages = sub.isMsg === true
  const submsges = !isBlocked && rowCanHaveSubmessages && sub.counts.submsges > 0 ? (
    <SubCountLink
      sub={sub}
      typeId={1}
      count={sub.counts.submsges}
      label="submsges"
      isInteractive={isInteractive}
    />
  ) : null
  const meta = isBlocked ? null : <SubMeta sub={sub} sourceMode={sourceMode} />

  return {
    display: {
      elId: sub.elId,
      title: sub.title,
      namekeySubject: sub.namekeySubject || sub.title,
      text: sub.text,
      datetime: sub.datetime == null ? '' : String(sub.datetime).trim(),
      href: subDirectHref(sub),
      codeKey: sub.codeKey,
      kind: sub.msgType ? String(sub.msgType) : '',
      viewType: sub.viewType == null ? '' : String(sub.viewType),
      isChecked: sub.flags.isChecked,
      isFavorite: sub.flags.isFavorite,
      isQuickReplyOpen: sub.flags.isQuickReplyOpen,
      isForeign: sub.legacy.data?.is_foreign === true,
      backgroundColor: scalarText(semanticDisplay.background_color) || '#ffffff',
      identity: {
        author: identityAuthor,
        address: identityAddress,
        from: fromName
          ? { name: fromName, href: scalarText(fromRec.href), isMsg: bool(fromRec.is_msg) }
          : undefined,
      },
      media: {
        avatarPath: sub.avatarPath || '',
        attachments,
        primaryAttachment: attachments[0],
      },
      access: {
        isLocked,
        isEditLocked,
        isTimeLocked,
        isContentLocked,
        hasEditPin,
        reason: grant
          ? (grant.reason === 'time' ? 'time' : grant.reason === 'show-pin' ? 'show' : grant.reason === 'edit-pin' || grant.reason === 'parent' ? 'edit' : null)
          : (isTimeLocked ? 'time' : isLocked ? 'show' : isEditLocked ? 'edit' : null),
        blocked: blockedInfo.blocked,
        blockedText: blockedInfo.text,
      },
      permissions: {
        canCheck: bool(permissions.can_check, true),
        // BLOCKED-ACCESS-MODEL: the emitted grant is authoritative when
        // present — it carries can_reply (never emitted by the legacy
        // namespace, which this bridge used to default to true) and the
        // corrected can_edit (the sub's OWN edit lock counts).
        canEdit: grant ? grant.capabilities.can_edit : bool(permissions.can_edit),
        canReply: isBlocked ? false : (grant ? grant.capabilities.can_reply : bool(permissions.can_reply, true)),
        canViewContent: grant ? grant.capabilities.can_view_content : bool(permissions.can_view_content, !isContentLocked),
      },
      email: {
        from: email.from,
        to: email.to,
        fromDisplay: scalarText(email.from_display),
      },
      state: {
        isRead: bool(semanticDisplay.is_read),
        isForeign: bool(semanticDisplay.is_foreign, sub.legacy.data?.is_foreign === true),
        isEmpty: [bool(rawIsEmpty[0]), bool(rawIsEmpty[1])],
        section: isSubmsges ? 'submsges' : 'answers',
      },
      counts: {
        answers: sub.counts.answers,
        submsges: sub.counts.submsges,
      },
    },
    controls: {
      check,
      favorite,
      quickReply,
      iconStrip,
      title,
      time,
      header,
      text,
      answers,
      submsges,
      emailFrom,
      meta,
      actions: { check, favorite, quickReply },
      content: { title, time, header, text },
      links: { answers, submsges },
      groups: { iconStrip, meta },
    },
    slots: {
      answers: !isBlocked && sub.children.answers.length > 0 ? (
        <SubFeed sub={sub} isSubmsges={false} childVariant={childVariant} />
      ) : null,
      submsges: !isBlocked && rowCanHaveSubmessages && sub.children.submsges.length > 0 ? (
        <SubFeed sub={sub} isSubmsges childVariant={childVariant} />
      ) : null,
      allChildren: isBlocked ? null : <SubChildren sub={sub} childVariant={childVariant} />,
    },
  }
}
